import { sandboxedHtmlDocument } from '../../../lib/sandboxed-html';
import { useTranslation } from '../../../lib/i18n';
import { useLoadBi } from '../../../lib/use-load-bi';
import { downloadArtifact } from '../../../lib/artifact-file';
import { docSource } from '../../../lib/doc-viewer';
import { DocumentTooLargeError, DocumentUnsupportedError, useConvertedDocument, type ConvertibleKind } from '../../../lib/doc-convert';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { effectiveContent } from '../../../lib/file-record';
import { Typography } from '../ui';
import { DocErrorState, DocShimmer, HandOffState } from './DocViewerStates';
import styles from './DocViewerModal.module.css';

// The two Office kinds — `.docx` and `.pptx` — land here. The pod converts the binary to markdown
// (`pages/api/convert-document.ts`), so this component is a LOADER, not a renderer: it fetches, handles the
// four states, and renders the returned document in a sandboxed frame.
//
// That is the whole design. Fidelity work for a format lives in ONE converter, and every improvement to the
// markdown view is inherited by both.
//
// `.xlsx` is here too: the SheetJS exclusion was about that PARSER, and a workbook is an OOXML zip read with
// the same unzip as a `.docx`. `.xls`/`.doc`/`.ppt` stay out — OLE compound files, which nothing here reads.
export function ConvertedDocView({ file, kind, onBi }: { file: WorkspaceFile; kind: ConvertibleKind; onBi: (action: string) => void }) {
  const { t } = useTranslation();
  const source = docSource(effectiveContent(file));
  const url = source?.kind === 'remote' ? source.url : null;
  const query = useConvertedDocument(url, kind);
  useLoadBi('doc-viewer-convert', { isLoading: query.isLoading, isError: query.isError }, { enabled: !!url });

  const download = () => {
    onBi('download');
    void downloadArtifact(file);
  };

  // No remote source at all: there is nothing to convert and no retry can change that.
  if (!url) return <HandOffState message={t('docViewer.unavailable')} onDownload={download} />;

  if (query.isLoading) return <DocShimmer />;

  // A document we reached but cannot read — a `.doc` misnamed `.docx`, a corrupt package, an empty deck.
  // Permanent, so it gets the hand-off rather than a Retry that would fail identically.
  if (query.error instanceof DocumentUnsupportedError) return <HandOffState message={t('docViewer.noPreview')} onDownload={download} />;
  if (query.error instanceof DocumentTooLargeError) return <HandOffState message={t('docViewer.tooLarge')} onDownload={download} />;

  if (query.isError || !query.data) {
    return (
      <DocErrorState
        onRetry={() => {
          onBi('retry');
          void query.refetch();
        }}
      />
    );
  }

  const { html, noticeKey } = query.data;

  return (
    <div className={styles.convertedBody}>
      {/* The fidelity limit, said out loud. A close rendering is useful; letting it pass for the real
          document is not — charts and a few style details do not survive. */}
      {noticeKey && (
        <Typography variant="p3" color="secondary" component="p">
          {t(noticeKey)}
        </Typography>
      )}
      {/* The rendered document, in the SAME sandbox the `html` kind uses: no scripts (sandbox) and no network
          (the CSP inside `sandboxedHtmlDocument`), because an Office file is untrusted content whichever
          format it arrived in. */}
      <iframe className={styles.renderedDocument} sandbox="" srcDoc={sandboxedHtmlDocument(html)} title="" />
    </div>
  );
}
